'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import {
  NAVIGATION_CONTEXTS,
  type NavigationContext,
} from '@/src/lib/navigation';
import { findWorkflow } from '@/src/lib/onboarding/workflows';
import { findTour } from '@/src/lib/onboarding/tours';
import { safeGetJson, safeSetJson } from '@/src/lib/safe-storage';
import { hasPermission } from '@/src/lib/permissions';

const RECENT_COMMANDS_KEY = 'kontora-cmd-palette-recent';
const MAX_RECENT_COMMANDS = 5;

export function readRecentCommands(): string[] {
  return safeGetJson<string[]>(RECENT_COMMANDS_KEY, []).slice(0, MAX_RECENT_COMMANDS);
}

export function recordRecentCommand(id: string): void {
  if (typeof window === 'undefined') return;
  const current = readRecentCommands().filter((cmd) => cmd !== id);
  const next = [id, ...current].slice(0, MAX_RECENT_COMMANDS);
  safeSetJson(RECENT_COMMANDS_KEY, next);
}

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  href?: string;
  action?: () => void;
  group: string;
  keywords?: string[];
}

export interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  items?: CommandItem[];
}

const WORKFLOW_CODES = [
  'wf-first-invoice',
  'wf-process-incoming',
  'wf-bank-import',
  'wf-monthly-close',
  'wf-invite-user',
  'wf-invite-advisor',
  'wf-system-backup',
] as const;

const TOUR_IDS = [
  'tour-overview',
  'tour-operations',
  'tour-accounting',
  'tour-advisor',
  'tour-tenant',
  'tour-system',
] as const;

export function buildDefaultItems(options: {
  availableContexts?: readonly NavigationContext[];
  permissions?: readonly string[];
  includeDevelopment?: boolean;
  onToggleTheme?: () => void;
} = {}): CommandItem[] {
  const contexts = options.availableContexts ?? Object.keys(NAVIGATION_CONTEXTS) as NavigationContext[];
  const navItems: CommandItem[] = Object.entries(NAVIGATION_CONTEXTS)
    .filter(([context]) => contexts.includes(context as NavigationContext))
    .map(([, ctx]) => ({
    id: `nav-${ctx.label}`,
    label: `Gehe zu ${ctx.label}`,
    description: ctx.description,
    href: ctx.href,
    group: 'Navigation',
  }));

  const workflowItems: CommandItem[] = WORKFLOW_CODES.filter((code) => {
    const workflow = findWorkflow(code);
    return workflow && contexts.includes(workflow.context) &&
      (options.permissions === undefined || hasPermission(options.permissions, workflow.requiredPermission));
  }).map((code) => {
    const wf = findWorkflow(code);
    return {
      id: `wf-${code}`,
      label: `Workflow: ${wf?.title ?? code}`,
      description: wf?.description,
      action: () => {
        const event = new CustomEvent('kontora:start-workflow', { detail: { code } });
        window.dispatchEvent(event);
      },
      group: 'Workflows',
    };
  });

  const tourItems: CommandItem[] = TOUR_IDS.filter((id) => {
    const tour = findTour(id);
    return tour && (!tour.context || contexts.includes(tour.context));
  }).map((id) => {
    const tour = findTour(id);
    return {
      id: `tour-${id}`,
      label: `Tour: ${tour?.title ?? id}`,
      description: tour?.description,
      action: () => {
        const event = new CustomEvent('kontora:start-tour', { detail: { id } });
        window.dispatchEvent(event);
      },
      group: 'Touren',
    };
  });

  const actionItems: CommandItem[] = [
    {
      id: 'action-theme',
      label: 'Theme wechseln',
      group: 'Aktionen',
      action: options.onToggleTheme ?? (() => window.dispatchEvent(new CustomEvent('kontora:toggle-theme'))),
    },
    {
      id: 'action-shortcuts',
      label: 'Tastatur-Übersicht',
      group: 'Aktionen',
      action: () => window.dispatchEvent(new CustomEvent('kontora:show-shortcuts')),
    },
    {
      id: 'action-devhub',
      label: 'Dev-Hub öffnen',
      description: 'Alle Engine-Demos durchsuchen',
      href: '/dev',
      group: 'Aktionen',
    },
  ];

  // Curated list of key engines for command palette (full list at /dev)
  const engineItems: CommandItem[] = [
    { id: 'eng-wiso', label: 'Engine: WISO/BüroEasy Import', href: '/dev/wiso-bueroeasy-import-engine-demo', group: 'Dev-Engines', keywords: ['wiso', 'büeroeasy', 'legacy', 'migration'] },
    { id: 'eng-maas', label: 'Engine: MaaS Mobility', href: '/dev/maas-mobility-service-engine-demo', group: 'Dev-Engines', keywords: ['maas', 'mobility', 'transport'] },
    { id: 'eng-grid', label: 'Engine: Smart Grid', href: '/dev/smart-grid-management-engine-demo', group: 'Dev-Engines', keywords: ['grid', 'energy', 'smart'] },
    { id: 'eng-defense', label: 'Engine: Defense (FAR/DFARS)', href: '/dev/defense-procurement-far-dfars-engine-demo', group: 'Dev-Engines', keywords: ['defense', 'far', 'dfars'] },
    { id: 'eng-climate', label: 'Engine: Carbon Offset Registry', href: '/dev/carbon-offset-registry-engine-demo', group: 'Dev-Engines', keywords: ['climate', 'carbon', 'registry'] },
    { id: 'eng-talent', label: 'Engine: Recruiting ATS', href: '/dev/recruiting-ats-engine-demo', group: 'Dev-Engines', keywords: ['recruiting', 'ats', 'hr'] },
    { id: 'eng-actuar', label: 'Engine: Actuarial Modeling', href: '/dev/actuarial-modeling-engine-demo', group: 'Dev-Engines', keywords: ['actuarial', 'insurance', 'solvv ii'] },
    { id: 'eng-fraud', label: 'Engine: Insurance Fraud Detection', href: '/dev/insurance-claims-fraud-detection-engine-demo', group: 'Dev-Engines', keywords: ['fraud', 'siu', 'claims'] },
    { id: 'eng-pqc', label: 'Engine: Post-Quantum Crypto', href: '/dev/post-quantum-cryptography-engine-demo', group: 'Dev-Engines', keywords: ['pqc', 'quantum', 'crypto'] },
    { id: 'eng-drone', label: 'Engine: Drone Operations U-Space', href: '/dev/drone-operations-u-space-engine-demo', group: 'Dev-Engines', keywords: ['drone', 'u-space'] },
    { id: 'eng-telemed', label: 'Engine: Telemedicine', href: '/dev/telemedicine-remote-care-engine-demo', group: 'Dev-Engines', keywords: ['telemedicine', 'health'] },
    { id: 'eng-digital', label: 'Engine: Digital Rights', href: '/dev/digital-rights-engine-demo', group: 'Dev-Engines', keywords: ['digital', 'rights', 'content'] },
    { id: 'eng-agri', label: 'Engine: Precision Agriculture', href: '/dev/precision-agriculture-engine-demo', group: 'Dev-Engines', keywords: ['agriculture', 'farming', 'iot'] },
    { id: 'eng-cat', label: 'Engine: Catastrophe Modeling', href: '/dev/catastrophe-modeling-engine-demo', group: 'Dev-Engines', keywords: ['catastrophe', 'nat cat', 'rms'] },
  ];

  const includeDevelopment = options.includeDevelopment ?? false;
  return [...navItems, ...engineItems, ...workflowItems, ...tourItems, ...actionItems]
    .filter((item) => includeDevelopment || !item.href?.startsWith('/dev'));
}

export function CommandPalette({ isOpen, onClose, items = buildDefaultItems() }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const filtered = (() => {
    if (!query.trim()) {
      const recent = readRecentCommands();
      if (recent.length > 0) {
        const orderedRecent = recent
          .map((id) => items.find((item) => item.id === id))
          .filter((item): item is CommandItem => Boolean(item));
        const remaining = items.filter((item) => !recent.includes(item.id));
        return [...orderedRecent, ...remaining].slice(0, 20);
      }
      return items.slice(0, 20);
    }
    const q = query.toLowerCase();
    return items
      .filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q) ||
          item.keywords?.some((k) => k.toLowerCase().includes(q)),
      )
      .slice(0, 20);
  })();

  function handleSelect(item: CommandItem) {
    recordRecentCommand(item.id);
    if (item.href) {
      router.push(item.href);
    }
    if (item.action) {
      item.action();
    }
    onClose();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) handleSelect(item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  const grouped = (() => {
    const map = new Map<string, CommandItem[]>();
    if (!query.trim()) {
      const recent = readRecentCommands();
      const orderedRecent = filtered.filter((item) => recent.includes(item.id));
      if (orderedRecent.length > 0) {
        map.set('Zuletzt verwendet', orderedRecent);
      }
      const otherGroups = filtered.filter((item) => !recent.includes(item.id))
        .reduce<Map<string, CommandItem[]>>((acc, item) => {
          if (!acc.has(item.group)) acc.set(item.group, []);
          acc.get(item.group)!.push(item);
          return acc;
        }, new Map());
      for (const [key, value] of otherGroups) map.set(key, value);
    } else {
      for (const item of filtered) {
        if (!map.has(item.group)) map.set(item.group, []);
        map.get(item.group)!.push(item);
      }
    }
    return map;
  })();

  if (!isOpen) return null;

  let globalIndex = 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Befehlspalette"
      data-tour="command-palette"
      className="fixed inset-0 z-[75] flex items-start justify-center bg-slate-900/55 px-4 pt-[12vh]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-[var(--k-radius-lg)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)]">
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Tippe einen Befehl oder suche…"
          aria-label="Befehlssuche"
          data-tour="command-palette-input"
          className="w-full border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-4 py-3 text-sm text-[var(--k-color-text-primary)] placeholder:text-[var(--k-color-text-muted)] focus:outline-none"
        />
        <ul role="listbox" className="max-h-[60vh] overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-[var(--k-color-text-muted)]">
              Keine Treffer für „{query}“.
            </li>
          ) : (
            Array.from(grouped.entries()).map(([group, groupItems]) => (
              <li key={group}>
                <p className="px-4 py-1 text-[10px] font-bold uppercase tracking-wide text-[var(--k-color-text-muted)]">
                  {group}
                </p>
                <ul>
                  {groupItems.map((item) => {
                    const idx = globalIndex++;
                    const isActive = idx === activeIndex;
                    return (
                      <li key={item.id} role="option" aria-selected={isActive}>
                        <button
                          type="button"
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          className={`flex w-full items-start gap-3 px-4 py-2 text-left text-sm transition ${
                            isActive
                              ? 'bg-[var(--k-color-accent-soft)] text-[var(--k-color-text-link)]'
                              : 'text-[var(--k-color-text-primary)]'
                          }`}
                        >
                          <span className="flex-1 min-w-0">
                            <span className="block font-semibold">{item.label}</span>
                            {item.description ? (
                              <span className="block text-xs text-[var(--k-color-text-secondary)]">
                                {item.description}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))
          )}
        </ul>
        <div className="flex items-center justify-between border-t border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-4 py-2 text-xs text-[var(--k-color-text-muted)]">
          <span>
            <kbd className="rounded bg-[var(--k-color-surface)] px-1.5 py-0.5 font-mono">↑↓</kbd>{' '}
            navigieren{' '}
            <kbd className="rounded bg-[var(--k-color-surface)] px-1.5 py-0.5 font-mono">
              Enter
            </kbd>{' '}
            auswählen
          </span>
          <span>
            <kbd className="rounded bg-[var(--k-color-surface)] px-1.5 py-0.5 font-mono">Esc</kbd>{' '}
            schließen
          </span>
        </div>
      </div>
    </div>
  );
}
